import { useCallback, useEffect, useState } from 'react';

/**
 * Tracks whether the on-page purchase CTA has scrolled ABOVE the viewport
 * (i.e. the user scrolled past it), used to toggle the sticky purchase bar.
 *
 * Returns a callback ref to attach to the CTA element and a boolean flag.
 * A callback ref (rather than a static RefObject) is used on purpose: it
 * re-observes whenever the target node changes, so the flag stays correct when
 * the CTA remounts — e.g. eyewear/strap variant switches remount the product
 * detail subtree via `key={sku}`.
 */
export const useCtaOffscreen = () => {
  const [node, setNode] = useState<Element | null>(null);
  const [offscreen, setOffscreen] = useState(false);

  const setRef = useCallback((el: Element | null) => setNode(el), []);

  useEffect(() => {
    if (!node || typeof IntersectionObserver === 'undefined') {
      // Reset when the CTA unmounts (e.g. variant switch) so a stale `true`
      // can't flash the bar at the top of the freshly loaded product before
      // the new observer fires.
      setOffscreen(false);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        // Only "off the top": not intersecting AND its top is above the
        // viewport. This keeps the bar hidden while the CTA is still below the
        // fold (not yet scrolled to).
        setOffscreen(!entry.isIntersecting && entry.boundingClientRect.top < 0);
      },
      { threshold: 0 },
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [node]);

  return [setRef, offscreen] as const;
};
